import { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock, MapPin, User, Mail, Phone, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const timeSlots = [
  "8:00 AM - 10:00 AM",
  "10:00 AM - 12:00 PM",
  "12:00 PM - 2:00 PM",
  "2:00 PM - 4:00 PM",
  "4:00 PM - 6:00 PM",
];

const BookingForm = ({ onSubmit, services }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "",
    preferred_time: "",
    address: "",
    special_requests: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!date || !formData.service_type || !formData.preferred_time) {
      return;
    }

    setIsSubmitting(true);
    const success = await onSubmit({
      ...formData,
      preferred_date: format(date, "yyyy-MM-dd"),
    });

    if (success) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_type: "",
        preferred_time: "",
        address: "",
        special_requests: "",
      });
      setDate(null);
    }
    setIsSubmitting(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8"
      data-testid="booking-form"
    >
      <div className="space-y-5">
        {/* Service Type */}
        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">Service Type</Label>
          <Select
            value={formData.service_type}
            onValueChange={(value) => handleSelectChange("service_type", value)}
          >
            <SelectTrigger
              className="h-12 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
              data-testid="service-type-select"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {services.map((service) => (
                <SelectItem key={service.id} value={service.id}>
                  {service.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date & Time Row */}
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Preferred Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={`w-full h-12 justify-start text-left font-normal rounded-xl border-slate-200 hover:bg-slate-50 ${
                    !date && "text-slate-500"
                  }`}
                  data-testid="date-picker-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4 text-[#2563EB]" />
                  {date ? format(date, "PPP") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                  data-testid="calendar"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time Slot */}
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Preferred Time</Label>
            <Select
              value={formData.preferred_time}
              onValueChange={(value) => handleSelectChange("preferred_time", value)}
            >
              <SelectTrigger
                className="h-12 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="time-select"
              >
                <Clock className="mr-2 h-4 w-4 text-[#2563EB]" />
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.map((slot) => (
                  <SelectItem key={slot} value={slot}>
                    {slot}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Name & Email Row */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                required
                className="h-12 pl-10 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="name-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                className="h-12 pl-10 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="email-input"
              />
            </div>
          </div>
        </div>

        {/* Phone & Address Row */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                required
                className="h-12 pl-10 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-700 font-medium">Address</Label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, River City"
                required
                className="h-12 pl-10 rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB]"
                data-testid="address-input"
              />
            </div>
          </div>
        </div>

        {/* Special Requests */}
        <div className="space-y-2">
          <Label className="text-slate-700 font-medium">Special Requests (Optional)</Label>
          <div className="relative">
            <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
            <Textarea
              name="special_requests"
              value={formData.special_requests}
              onChange={handleChange}
              placeholder="Any special instructions or areas to focus on..."
              className="pl-10 min-h-[100px] rounded-xl border-slate-200 focus:ring-[#2563EB]/20 focus:border-[#2563EB] resize-none"
              data-testid="special-requests-input"
            />
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={isSubmitting || !date || !formData.service_type || !formData.preferred_time}
          className="w-full h-14 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full text-lg font-semibold btn-glow transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="booking-submit-btn"
        >
          {isSubmitting ? "Submitting..." : "Request Quote"}
        </Button>

        <p className="text-center text-sm text-slate-500">
          We'll get back to you within 24 hours with a personalized quote.
        </p>
      </div>
    </form>
  );
};

export default BookingForm;
